import {
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { type ReactNode, useState } from "react";

import { CALIBRATE_COLORS } from "components/hoc/Theme";

export interface RowMenuItem {
  key: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
  /** A write Client View does not allow: shown, but off. */
  readOnly?: boolean;
  /** A line above this item. */
  dividerBefore?: boolean;
}

/** A row's icon buttons, darker than MUI's grey so they read at a glance. */
export const ROW_ICON_SX = { color: "text.secondary" } as const;

/** The footprint of one small icon button in a row. */
export const ROW_BUTTON_PX = 30;

/** A destructive menu item: red text and the red hover of Logout. */
export const DANGER_MENU_ITEM_SX = {
  color: "error.main",
  "&:hover, &.Mui-focusVisible": { bgcolor: CALIBRATE_COLORS.dangerHover },
} as const;

/** The header of the Actions column; its cells centre the buttons under it. */
export function ActionsHeaderCell() {
  return (
    <TableCell
      align="center"
      sx={{ bgcolor: "grey.100", width: 48, whiteSpace: "nowrap" }}
    >
      Actions
    </TableCell>
  );
}

export function ActionsCell({ children }: { children?: ReactNode }) {
  return (
    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
      {children}
    </TableCell>
  );
}

/** The three dots at the end of a row. */
export default function RowMenu({
  label,
  items,
  disabled = false,
}: {
  label: string;
  items: RowMenuItem[];
  disabled?: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (items.length === 0) return null;
  return (
    <>
      <IconButton
        size="small"
        aria-label={label}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
        sx={ROW_ICON_SX}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {items.flatMap((item) => [
          ...(item.dividerBefore ? [<Divider key={`${item.key}-line`} />] : []),
          <MenuItem
            key={item.key}
            disabled={item.readOnly}
            onClick={() => {
              setAnchor(null);
              item.onClick();
            }}
            sx={item.danger ? DANGER_MENU_ITEM_SX : undefined}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
}
